import { type MobileVideo, type MobileVideoDownload } from "./mobileVideoStore";

/**
 * Expected byte size of the file THIS download fetched (or will fetch).
 *
 * The signed download URL serves the 720p rendition when one exists, whose
 * size differs from the catalog's `sizeBytes` (always the original upload).
 * The engine stamps the served file's size on the download record at
 * start time; verification must use that stamp, or every rendition
 * download would be deleted as "corrupt" against the original's size.
 *
 * Three-valued stamp on `download.expectedSizeBytes`:
 *   number    — verify against exactly this
 *   null      — served file's size unknown; verification impossible
 *   undefined — record predates per-download stamps: those downloads
 *               fetched the original, so the catalog size applies
 *               (a falsy catalog size means unknown — see getVideos,
 *               which serves a null DB size as 0)
 *
 * Returns the size to verify against, or null when unverifiable.
 */
export function expectedDownloadSizeBytes(
  item: Pick<MobileVideo, "sizeBytes" | "download">,
): number | null {
  const stamped = item.download?.expectedSizeBytes;

  if (stamped !== undefined) {
    return stamped;
  }

  return item.sizeBytes || null;
}

/**
 * Size to DISPLAY for a download row/total. Preference order:
 *
 *   1. the served file's stamped size (this download's ground truth);
 *   2. the catalog's `downloadSizeBytes` — the server-known size a download
 *      WILL transfer (the 720p rendition when one exists), so totals are
 *      right before any download has started instead of shrinking as stamps
 *      arrive. Its 0 means the server knows a rendition will be served but
 *      not yet its size — that stays unknown (null) rather than guessing
 *      with the original's size, which describes a file the download won't
 *      fetch;
 *   3. EXCEPTION to 2: a COMPLETED download with an absent (undefined) stamp
 *      predates per-download stamps, and those downloads fetched the
 *      original upload — the bytes on disk are the original's, so that row
 *      displays (and the aggregate sums) the original size, mirroring
 *      `expectedDownloadSizeBytes`' verification rule. Queued/just-started
 *      records are also briefly unstamped, but they'll fetch the rendition,
 *      so only completion makes the absent stamp meaningful;
 *   4. the catalog's original-upload `sizeBytes`, for rows cached before
 *      `downloadSizeBytes` existed (0 there means unknown too).
 *
 * This is the DISPLAY rule, deliberately looser than `expectedDownloadSizeBytes`
 * (the VERIFICATION rule, which returns null when the served size is unknown so
 * a download is never checked against a guessed size): here we'd rather show
 * a best estimate than show nothing. Keep the Downloads dialog's per-row
 * sizes and the Download All aggregate on this one helper so they can't drift.
 *
 * `download` is passed explicitly rather than read off the video, because both
 * call sites take the authoritative download record from the device-global
 * store (a video mirrored into an impersonation view carries a null download).
 */
export function displayDownloadSizeBytes(
  download:
    | Pick<MobileVideoDownload, "expectedSizeBytes" | "status">
    | null
    | undefined,
  video: Pick<MobileVideo, "sizeBytes" | "downloadSizeBytes">,
): number | null {
  const stamped = download?.expectedSizeBytes;

  if (stamped != null) {
    return stamped;
  }

  if (stamped === undefined && download?.status === "completed") {
    return video.sizeBytes || null;
  }

  if (video.downloadSizeBytes != null) {
    return video.downloadSizeBytes || null;
  }

  return video.sizeBytes || null;
}

/**
 * Whether the on-disk file's size is consistent with what this download
 * was expected to fetch. Unverifiable downloads (no usable expected size)
 * are treated as matching.
 */
export function downloadSizeMatches(
  item: Pick<MobileVideo, "sizeBytes" | "download">,
  statSizeBytes: number | undefined,
): boolean {
  const expected = expectedDownloadSizeBytes(item);

  return expected == null || statSizeBytes === expected;
}

/**
 * Whether this download's bytes predate the catalog's current media file
 * (the recording was replaced after the download). With per-download size
 * stamps, a size check can no longer catch replacements — the old file
 * always matches its own stamp — so launch reconcile needs this generation
 * comparison to delete-and-requeue stale bytes, restoring the self-heal
 * the catalog-size comparison used to provide by accident.
 *
 * Mirrors the player's staleness rules (see VideoPlayer): the download's
 * stamp comes from the signed-URL response, which can run AHEAD of a stale
 * catalog row — a newer stamp is current, not stale. Only an older (or
 * stamped-null) generation marks the file stale. Legacy records without a
 * generation stamp can't be judged and are treated as current (their size
 * check against the catalog still catches replacements, as before).
 */
export function isDownloadGenerationStale(
  item: Pick<MobileVideo, "fileUploadedAt" | "download">,
): boolean {
  const catalogGeneration = item.fileUploadedAt;
  const downloadGeneration = item.download?.fileUploadedAt;

  if (catalogGeneration == null || downloadGeneration === undefined) {
    return false;
  }

  return downloadGeneration === null || downloadGeneration < catalogGeneration;
}

export type ServedFileStamps = {
  fileUploadedAt: string | null | undefined;
  expectedSizeBytes: number | null | undefined;
  contentType: string | null | undefined;
};

/**
 * The stamps a signed-URL response puts on the download record. Every stamp
 * mirrors the response exactly — present values (including explicit null,
 * which keeps its server-assigned meaning: generation-less legacy upload /
 * unsized file) are stamped; absent fields (an OLD server with no
 * served-file contract, which by definition serves the original) clear the
 * stamp to undefined, the unstamped state whose legacy heuristics apply.
 *
 * Both coercions this replaces caused retry-forever loops: absent→null on
 * the size meant "unverifiable" (error bodies complete), absent→null on the
 * generation meant "predates the catalog" (valid downloads rejected), and
 * skipping absent fields preserved a previous attempt's stale stamps across
 * a server rollback.
 */
export function stampsFromDownloadUrlResponse(response: {
  fileUploadedAt?: string | null;
  sizeBytes?: number | null;
  contentType?: string | null;
}): ServedFileStamps {
  return {
    fileUploadedAt: response.fileUploadedAt,
    expectedSizeBytes: response.sizeBytes,
    contentType: response.contentType,
  };
}
